import * as THREE from "three";
import { squareToRowCol } from "../scene/boardCoordinates";

export type TileFamily = "yellow" | "green";

/**
 * The board's permanent checkerboard identity. Fixed by square coordinates
 * alone, decided once, and never touched by visit order, tour index, or
 * animation progress: `(row + col)` even is the yellow family, odd is the
 * green family. The family a tile belongs to is an invariant of its
 * position, not a function of time -- this is what keeps the checkerboard
 * readable no matter how far a tour, or how many tours, have played.
 */
export function getTileFamily(row: number, column: number): TileFamily {
  return (row + column) % 2 === 0 ? "yellow" : "green";
}

export function tileFamilyForSquare(square: number): TileFamily {
  const { row, col } = squareToRowCol(square);
  return getTileFamily(row, col);
}

export interface FamilyColorPair {
  yellow: THREE.Color;
  green: THREE.Color;
}

/**
 * A designed, cyclic sequence of color pairs the board works through, one
 * pair per tour. Tour N rests on `PALETTE[N % length]` and its visited
 * color is exactly `PALETTE[(N + 1) % length]` -- so the pair a tour ends
 * on is the exact pair the next tour starts from (no jarring reset), and
 * after `length` tours the sequence wraps back to the first pair. Within a
 * single tour, a tile still only ever shows one of these two colors -- its
 * family's rest color or its family's visited color -- snapped directly,
 * never interpolated (see `Board3D.applyPermanentVisitColor`); it is only
 * *across* tours that the palette itself advances.
 */
const PALETTE: readonly { name: string; yellow: number; green: number }[] = [
  { name: "Sunrise", yellow: 0xffd84a, green: 0x56c271 },
  { name: "Ember", yellow: 0xe53935, green: 0x2457d6 },
  { name: "Sunset", yellow: 0xff8a00, green: 0x6c3baa },
  { name: "Aurora", yellow: 0xff4fa3, green: 0x12b8a0 },
  { name: "Midnight Gold", yellow: 0xffe066, green: 0x7209b7 },
  { name: "Coral Reef", yellow: 0xff5a36, green: 0x1e96fc },
];

export function paletteLength(): number {
  return PALETTE.length;
}

function wrapIndex(index: number, length: number): number {
  return ((index % length) + length) % length;
}

/** Palette entry at `index`, wrapped to a valid array position for any integer (including negative). */
export function paletteEntry(index: number): { name: string; yellow: THREE.Color; green: THREE.Color } {
  const entry = PALETTE[wrapIndex(index, PALETTE.length)];
  return { name: entry.name, yellow: new THREE.Color(entry.yellow), green: new THREE.Color(entry.green) };
}

/** The pair a tour rests on (offset 0) or ends on / the next tour starts from (offset 1). */
function tourColorPair(tourIndex: number, offset: 0 | 1): FamilyColorPair {
  const entry = paletteEntry(tourIndex + offset);
  return { yellow: entry.yellow, green: entry.green };
}

/** The resting (unvisited) color for a tile of the given family, in the given tour. */
export function familyRestColor(family: TileFamily, tourIndex: number): THREE.Color {
  const pair = tourColorPair(tourIndex, 0);
  return family === "yellow" ? pair.yellow : pair.green;
}

/**
 * The color a tile of the given family snaps to the moment it is visited,
 * in the given tour -- exactly the *next* pair in the palette sequence, so
 * a completed tour's visited colors are already this tour's next starting
 * colors (see `Board3D.resetBoardColors`, which is called with `tourIndex
 * + 1` for the following tour).
 */
export function familyEndColor(family: TileFamily, tourIndex: number): THREE.Color {
  const pair = tourColorPair(tourIndex, 1);
  return family === "yellow" ? pair.yellow : pair.green;
}

/** Temporary landing emphasis only -- never mixed into the permanent tile color. */
export const COMPLETION_PULSE_COLOR = new THREE.Color(0xf2c14e);
